import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Typography,

  Paper,
  
  IconButton,
  
  TextField,

  Stack,
  Dialog,
  DialogContent

} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  MyLocation as LocationIcon,
  SwapVert as SwapIcon,
  FlashOn as LightningIcon,
  RadioButtonChecked as GreenDotIcon,
  Place as RedPinIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// EV Advanced Components
import VehicleSelection from './VehicleSelection';
import ChargingStrategy from './ChargingStrategy';
import BatteryOptionsSection from './BatteryOptionsSection';
import StationSearchSettingsSection from './StationSearchSettingsSection';
import AdvancedOptionsToggle from './AdvancedOptionsToggle';
import AmenitiesSection from './AmenitiesSection';
import VehicleDetailsSection from './VehicleDetailsSection';
// import EnhancedVehicleSelectionModal from './EnhancedVehicleSelectionModal'; // Using inline selection instead
import InlineVehicleSelection from './InlineVehicleSelection';
// Removed unused calculateChargingStops and CHARGING_STRATEGIES imports
// import { fetchExternalChargers, filterChargersOnRoute } from './utils/externalChargers';
import { filterMapChargersAlongRoute } from './utils/simpleRouteChargers';
import { useMapContext } from '../../context/MapContext';

const RoutePlannerPanel = ({ onClose, onCalculateRoute }) => {
  const context = useMapContext();
  const { 
    routeWaypoints, 
    setRouteWaypoints, 
    isRoutePlanning,
    setIsRoutePlanning,
    chargers: mapChargers,
    filteredChargers,
    routePlannerMode,
    setRoutePlannerMode
  } = context || {};

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelSurface = isDark
    ? alpha(theme.palette.background.paper, 0.96)
    : theme.palette.common.white;
  const panelBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : '1px solid rgba(0, 0, 0, 0.05)';
  const headerGradient = isDark
    ? 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(15,15,35,0.9) 100%)'
    : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)';
  const toggleBackground = isDark
    ? alpha(theme.palette.common.white, 0.05)
    : '#f5f5f5';
  const toggleActiveBg = isDark
    ? alpha(theme.palette.primary.main, 0.25)
    : theme.palette.common.white;
  const toggleInactiveColor = isDark
    ? alpha(theme.palette.common.white, 0.7)
    : '#6b7280';
  const inputBackground = isDark
    ? alpha(theme.palette.background.paper, 0.92)
    : theme.palette.common.white;
  const inputBorderColor = isDark
    ? alpha(theme.palette.common.white, 0.15)
    : '#e0e0e0';
  const dropdownBackground = isDark
    ? alpha(theme.palette.background.paper, 0.98)
    : theme.palette.common.white;
  const dropdownBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
    : '1px solid #e0e0e0';
  const elevatedShadow = isDark
    ? `0 24px 60px ${alpha(theme.palette.common.black, 0.85)}`
    : '0 20px 40px rgba(0, 0, 0, 0.15)';
  const brandGradient = theme.palette.gradients.brand;
  const brandHoverGradient = theme.palette.gradients.brandHover;
  const brandShadow = isDark
    ? '0 16px 40px rgba(6, 11, 25, 0.75)'
    : '0 14px 30px rgba(59, 130, 246, 0.35)';
  const disabledButtonBg = isDark
    ? alpha(theme.palette.common.white, 0.08)
    : theme.palette.grey[300];
  const disabledButtonColor = isDark
    ? alpha(theme.palette.common.white, 0.45)
    : theme.palette.grey[500];

  // Use routeWaypoints from context, with proper initialization
  const waypoints = routeWaypoints && routeWaypoints.length > 0 
    ? routeWaypoints 
    : [
        { id: 1, location: '', coordinates: null },
        { id: 2, location: '', coordinates: null },
      ];
  const validWaypointCount = waypoints.filter(wp => wp.coordinates).length;
  const canPlanRoute = validWaypointCount >= 2;

  // Safe wrapper for setWaypoints
  const setWaypoints = (updater) => {
    if (setRouteWaypoints) {
      if (typeof updater === 'function') {
        setRouteWaypoints(prev => {
          const currentWaypoints = prev && prev.length > 0 
            ? prev 
            : [
                { id: 1, location: '', coordinates: null },
                { id: 2, location: '', coordinates: null },
              ];
          return updater(currentWaypoints);
        });
      } else {
        setRouteWaypoints(updater);
      }
    }
  };

  // Use mode from context instead of local state
  const mode = routePlannerMode || 'simple';
  const setMode = setRoutePlannerMode || (() => {});
  // Removed duplicate waypoints state - using context instead
  // Removed separate isCalculating state; using isRoutePlanning from context instead
  // const [isCalculating, setIsCalculating] = useState(false);
  const [predictions, setPredictions] = useState({});
  
  // EV Advanced State
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [batteryPercentage, setBatteryPercentage] = useState(80);
  const [selectedStrategy, setSelectedStrategy] = useState('quickest');
  // const [showVehicleModal, setShowVehicleModal] = useState(false); // Using inline selection instead
  const [showVehicleSelectionModal, setShowVehicleSelectionModal] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showBatteryOptions, setShowBatteryOptions] = useState(false);
  const [showStationSettings, setShowStationSettings] = useState(false);
  const [showAmenities, setShowAmenities] = useState(false);
  
  // EV Advanced Settings
  const [batteryCapacity, setBatteryCapacity] = useState(60);
  const [departureSoC, setDepartureSoC] = useState(80);
  const [arrivalSoC, setArrivalSoC] = useState(20);
  const [stationRadius, setStationRadius] = useState(5);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  
  // Vehicle Details Settings with defaults as specified
  const [numberOfPassengers, setNumberOfPassengers] = useState(2); // Default: 2 passengers
  const [avgPassengerWeight, setAvgPassengerWeight] = useState(75); // Default: 75kg per passenger (150kg total for 2)
  const [luggageWeight, setLuggageWeight] = useState(120); // Default: 120kg luggage
  const [vehicleWeight, setVehicleWeight] = useState(1500); // Default: 1500kg vehicle weight
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);

  useEffect(() => {
    if (window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    
    // Initialize waypoints if they don't exist
    if (setRouteWaypoints && (!routeWaypoints || routeWaypoints.length === 0)) {
      setRouteWaypoints([
        { id: 1, location: '', coordinates: null },
        { id: 2, location: '', coordinates: null },
      ]);
    }
  }, [routeWaypoints, setRouteWaypoints]);

  const handleLocationInput = (id, value) => {
    if (setWaypoints) {
      setWaypoints(prev =>
        prev.map(wp => (wp.id === id ? { ...wp, location: value } : wp))
      );
    }

    if (value.length > 2 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'IN' }, sessionToken: sessionToken.current },
        (results, status) => {
          if (status === 'OK' && results) {
            setPredictions(prev => ({ ...prev, [id]: results }));
          }
        }
      );
    } else {
      setPredictions(prev => ({ ...prev, [id]: [] }));
    }
  };

  const handleSelectPrediction = async (waypointId, prediction) => {
    const geocoder = new window.google.maps.Geocoder();
    const result = await new Promise((resolve, reject) => {
      geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === 'OK' && results?.[0]) resolve(results[0]);
        else reject(status);
      });
    });

    const coordinates = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };

    if (setWaypoints) {
      setWaypoints(prev =>
        prev.map(wp =>
          wp.id === waypointId
            ? { ...wp, location: prediction.description, coordinates }
            : wp
        )
      );
    }
    setPredictions(prev => ({ ...prev, [waypointId]: [] }));
    sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setWaypoints(prev =>
                prev.map((wp, idx) =>
                  idx === 0
                    ? { ...wp, location: results[0].formatted_address, coordinates: { lat: latitude, lng: longitude } }
                    : wp
                )
              );
            }
          });
        },
        (error) => {
          alert('Unable to get your current location. Please check your browser permissions.');
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const swapWaypoints = () => {
    if (waypoints.length >= 2 && setWaypoints) {
      setWaypoints([
        { ...waypoints[1], id: waypoints[0].id },
        { ...waypoints[0], id: waypoints[1].id },
        ...waypoints.slice(2),
      ]);
    }
  };

  // addWaypoint currently unused in this panel
  // const addWaypoint = () => {
  //   if (setWaypoints) {
  //     setWaypoints(prev => [
  //       ...prev.slice(0, -1),
  //       { id: Date.now(), location: '', coordinates: null },
  //       prev[prev.length - 1],
  //     ]);
  //   }
  // };

  // removeWaypoint currently unused
  // const removeWaypoint = (id) => {
  //   if (waypoints.length > 2 && setWaypoints) {
  //     setWaypoints(prev => prev.filter(wp => wp.id !== id));
  //   }
  // };

  // EV Advanced Handlers
  const handleVehicleSelection = () => {
    setShowVehicleSelectionModal(true);
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setBatteryCapacity(vehicle.batteryCapacity);
    setBatteryPercentage(80); // Reset to 80% when selecting new vehicle
  };

  const handleStrategyChange = (strategy) => {
    setSelectedStrategy(strategy);
  };

  // Calculate route function
  async function calculateRoute() {
    
    // Force an alert to prove function entry
    if (typeof window !== 'undefined' && window.alert) {
      setTimeout(() => alert('calculateRoute function called!'), 100);
    }
    
    // First, geocode any waypoints that don't have coordinates
    const geocodedWaypoints = await Promise.all(
      waypoints.map(async (waypoint) => {
        if (waypoint.coordinates) {
          return waypoint;
        }
        
        if (!waypoint.location.trim()) {
          return waypoint;
        }
        
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: waypoint.location }, (results, status) => {
              if (status === 'OK' && results?.[0]) {
                resolve(results[0]);
              } else {
                reject(new Error(`Geocoding failed: ${status}`));
              }
            });
          });
          
          const coordinates = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
          
          return {
            ...waypoint,
            coordinates,
            location: result.formatted_address
          };
        } catch (error) {
          return waypoint;
        }
      })
    );
    
    const validWaypoints = geocodedWaypoints.filter(wp => wp.coordinates);
    
    if (validWaypoints.length < 2) {
      const failedWaypoints = geocodedWaypoints.filter(wp => !wp.coordinates && wp.location.trim());
      if (failedWaypoints.length > 0) {
        alert(`Unable to find coordinates for: ${failedWaypoints.map(wp => wp.location).join(', ')}\n\nPlease try selecting from the dropdown suggestions or use more specific addresses.`);
      } else {
        alert('Please enter at least start and end locations with valid addresses');
      }
      return;
    }

    // For EV Advanced mode, ensure vehicle is selected
    if (mode === 'ev_advanced' && !selectedVehicle) {
      alert('Please select your vehicle first');
      return;
    }

    if (setIsRoutePlanning) {
      setIsRoutePlanning(true);
    }
    
    try {
      const directionsService = new window.google.maps.DirectionsService();
      const result = await new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: validWaypoints[0].coordinates,
            destination: validWaypoints[validWaypoints.length - 1].coordinates,
            waypoints: validWaypoints.slice(1, -1).map(wp => ({
              location: wp.coordinates,
              stopover: true,
            })),
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            
            if (status === 'OK') {
              resolve(result);
            } else {
              reject(status);
            }
          }
        );
      });

      let routeData = {
        route: result,
        waypoints: validWaypoints,
        mode,
        distance: result.routes[0].legs.reduce((sum, leg) => sum + leg.distance.value, 0) / 1000, // Convert to km
        duration: result.routes[0].legs.reduce((sum, leg) => sum + leg.duration.value, 0) / 60, // Convert to minutes
      };
      
      // Detailed per-leg debug (commented to reduce noise)
      // const legDetails = result.routes[0].legs.map((leg, index) => ({
      //   distance_km: leg.distance.value / 1000,
      //   duration_minutes: leg.duration.value / 60
      // }));
      
      const totalDistanceMeters = result.routes[0].legs.reduce((sum, leg) => sum + leg.distance.value, 0);
      const totalDistanceKm = totalDistanceMeters / 1000;
      const totalDurationSeconds = result.routes[0].legs.reduce((sum, leg) => sum + leg.duration.value, 0);
      const totalDurationMinutes = totalDurationSeconds / 60;
      


      // For SIMPLE mode, find charging stations along the route
      if (mode === 'simple') {
        
        try {
          // Get route path for filtering existing map chargers
          const routePath = result.routes[0].overview_path;
          
          // Use either chargers or filteredChargers from context
          const availableChargers = mapChargers || filteredChargers || [];
          
          if (availableChargers.length === 0) {
            routeData.chargingStations = [];
            routeData.chargers = [];
          } else {
            // Filter existing map chargers along the route (5km buffer)
            const routeChargers = filterMapChargersAlongRoute(availableChargers, routePath, 5);
            
            routeData.chargingStations = routeChargers;
            routeData.chargers = routeChargers; // Also set chargers for compatibility
            
            if (routeChargers.length === 0) {
            }
          }
        } catch (error) {
          routeData.chargingStations = [];
          routeData.chargers = [];
        }
      }

      // For EV Advanced mode, calculate smart charging stops
      
      
      // FORCE ALERT TO CHECK CONDITION
      
      if (mode === 'ev_advanced') {
      }
      
      if (mode === 'ev_advanced' && selectedVehicle) {
        
        try {
          // Prepare vehicle options for new EV planner with dynamic weight calculations
          const totalPassengerWeight = numberOfPassengers * avgPassengerWeight;
          const totalVehicleWeight = selectedVehicle.weight + totalPassengerWeight + luggageWeight;
          const weightFactor = totalVehicleWeight / selectedVehicle.weight;
          const adjustedConsumption = (selectedVehicle.consumption || (selectedVehicle.batteryCapacity / selectedVehicle.range)) * weightFactor;
          
          const vehicleOptions = {
            batteryCapacityKwh: selectedVehicle.batteryCapacity,
            initialSoC: batteryPercentage,
            minSoC: arrivalSoC || 20,
            consumptionKwhPerKm: adjustedConsumption,
            fastChargingPower: selectedVehicle.fastChargingPower || 150,
            vehicleRange: selectedVehicle.range,
            vehicleWeight: selectedVehicle.weight || 1500,
            totalWeight: totalVehicleWeight,
            passengerWeight: totalPassengerWeight,
            luggageWeight: luggageWeight,
            numberOfPassengers: numberOfPassengers,
            avgPassengerWeight: avgPassengerWeight,
            searchRadius: stationRadius || 50
          };


          // Use coordinates from first and last waypoints
          const start = validWaypoints[0].coordinates;
          const end = validWaypoints[validWaypoints.length - 1].coordinates;


          let evRoutePlan;
          // Derive routePath similar to SIMPLE mode for charger proximity filtering
          let routePath = [];
          try {
            routePath = result.routes[0].overview_path || [];
          } catch (rpErr) {
          }
          try {
            // Import the enhanced EV planner
            const enhancedEvModule = await import('./utils/enhancedEvPlanner');
            const { calculateEnhancedEvRoute, createEnergyPlan } = enhancedEvModule;
            
            // Prepare vehicle data with correct structure for enhancedEvPlanner
            const vehicleData = {
              batteryCapacity: selectedVehicle.batteryCapacity,
              range: selectedVehicle.range,
              consumption: selectedVehicle.consumption || (selectedVehicle.batteryCapacity / selectedVehicle.range * 1000), // Wh/km
              weight: selectedVehicle.weight || 1500, // Base vehicle weight
            };
            
            // Prepare trip options (use defaults when advanced options are disabled)
            const tripOptions = {
              passengers: showVehicleDetails ? numberOfPassengers : 2,
              luggageWeight: showVehicleDetails ? luggageWeight : 120,
              additionalWeight: showVehicleDetails ? totalPassengerWeight : 150,
              currentSOC: batteryPercentage,
              targetSOC: 85,
              arrivalSOC: arrivalSoC || 20, // Separate arrival SOC requirement
              chargingStrategy: selectedStrategy,
              allowSynthetic: false // Disable synthetic/fallback demo charging stops
            };
            
            
            // Build charger candidate list (mapChargers first, fallback filteredChargers)
            let availableChargers = mapChargers || filteredChargers || [];
            // If we have routePath and chargers, narrow to chargers along path (like SIMPLE mode)
            if (availableChargers.length > 0 && routePath.length > 0) {
              const alongRoute = filterMapChargersAlongRoute(availableChargers, routePath, 25); // Widen buffer to 25km to capture real chargers
              if (alongRoute.length > 0) {
                availableChargers = alongRoute;
              } else {
              }
            } else if (availableChargers.length === 0) {
            }
            
            // Set chargers in window context for enhanced EV planner
            if (availableChargers.length > 0) {
              window.__MAP_CONTEXT_CHARGERS = availableChargers;
            } else {
            }

            // Use the enhanced EV route planner
            // Call the enhanced EV route planner
            
            let enhancedResult;
            try {
              enhancedResult = await calculateEnhancedEvRoute(routeData.route, vehicleData, tripOptions, availableChargers);
              
              if (enhancedResult && enhancedResult.chargingStops) {
              } else {
              }
            } catch (enhancedError) {
              throw enhancedError; // Re-throw to trigger fallback
            }
            
            // Convert to expected format
            evRoutePlan = {
              directionsResult: routeData.route,
              route: routeData.route,
              routeHealth: { isHealthy: true, warnings: [] },
              totalDistance: enhancedResult.totalDistance,
              totalDuration: enhancedResult.totalTripTime,
              totalTripTime: enhancedResult.totalTripTime,
              chargingStops: enhancedResult.chargingStops,
              finalSoC: enhancedResult.finalSOC,
              finalSoCStatus: 'calculated',
              totalChargingTime: enhancedResult.totalChargingTime,
              energyConsumed: enhancedResult.energyConsumed,
              availableEnergy: enhancedResult.availableEnergy,
              requiredEnergy: enhancedResult.requiredEnergy,
              energyDeficit: enhancedResult.energyDeficit,
              needsCharging: enhancedResult.energyDeficit > 0,
              isRoutePossible: enhancedResult.isRoutePossible,
              vehicleCanReachWithoutCharging: enhancedResult.energyDeficit <= 0,
              adjustedConsumption: enhancedResult.adjustedConsumption,
              energyPlan: createEnergyPlan(enhancedResult),
              // Include vehicle data and enhanced calculations for RouteDetailsPanel
              selectedVehicle: selectedVehicle,
              batteryPercentage: batteryPercentage,
              chargingStrategy: selectedStrategy,
              vehicleOptions: enhancedResult.vehicleOptions,
              enhancedCalculations: {
                totalWeight: totalVehicleWeight,
                adjustedConsumption: adjustedConsumption,
                passengers: numberOfPassengers,
                luggageWeight: luggageWeight,
                additionalWeight: totalPassengerWeight,
                baseWeight: selectedVehicle.weight || 1500,
                consumptionRate: adjustedConsumption * 1000, // Wh/km
                avgPassengerWeight: avgPassengerWeight
              }
            };

            // Normalize chargingStops with explicit lat/lng for MapContainer marker logic
            if (Array.isArray(evRoutePlan.chargingStops)) {
              evRoutePlan.chargingStops = evRoutePlan.chargingStops.map((stop) => {
                // Derive lat/lng from various possible shapes
                let lat = stop.lat || stop.locationLatitude || null;
                let lng = stop.lng || stop.locationLongitude || null;
                // If location has coordinates array [lng, lat]
                if ((!lat || !lng) && stop.location?.coordinates?.length === 2) {
                  lng = stop.location.coordinates[0];
                  lat = stop.location.coordinates[1];
                }
                // If location is a Google LatLng object
                if ((!lat || !lng) && stop.location) {
                  try {
                    if (typeof stop.location.lat === 'function') lat = stop.location.lat();
                    if (typeof stop.location.lng === 'function') lng = stop.location.lng();
                  } catch (e) {
                  }
                }
                // Fallback: leave undefined if still missing
                if (lat == null || lng == null) {
                }
                const coordinatesArray = (lat != null && lng != null) ? [lng, lat] : stop.location?.coordinates;
                return {
                  ...stop,
                  lat,
                  lng,
                  locationLatitude: lat,
                  locationLongitude: lng,
                  location: {
                    ...(stop.location || {}),
                    coordinates: coordinatesArray || stop.location?.coordinates || []
                  }
                };
              });
            }
            
            // If planner returned zero charging stops but energy deficit exists, derive heuristic stops from filtered chargers
            // Heuristic demo stop generation DISABLED (only real chargers allowed)
            const ALLOW_HEURISTIC_STOPS = false;
            if (ALLOW_HEURISTIC_STOPS && (evRoutePlan.chargingStops?.length === 0 || evRoutePlan.chargingStops?.every(s => !s.lat || !s.lng)) && evRoutePlan.energyDeficit > 0) {
              const heuristicStops = [];
              const chargersForHeuristic = availableChargers.slice(0, 12); // limit
              const distanceKm = routeData.distance;
              const vehicleRangeKm = selectedVehicle.range || 300;
              const neededStops = Math.max(1, Math.ceil(distanceKm / vehicleRangeKm) - 1);
              for (let i = 0; i < neededStops; i++) {
                const charger = chargersForHeuristic[i % chargersForHeuristic.length];
                if (!charger) break;
                // Extract coordinates from charger
                let hLat = charger.location?.coordinates?.[1] || charger.locationLatitude || charger.lat || charger.latitude;
                let hLng = charger.location?.coordinates?.[0] || charger.locationLongitude || charger.lng || charger.longitude;
                if (!hLat && charger.location && typeof charger.location.lat === 'function') hLat = charger.location.lat();
                if (!hLng && charger.location && typeof charger.location.lng === 'function') hLng = charger.location.lng();
                heuristicStops.push({
                  id: `heuristic_${i+1}`,
                  name: charger.name || `Heuristic Stop ${i+1}`,
                  location: {
                    ...(charger.location || {}),
                    coordinates: (hLat != null && hLng != null) ? [parseFloat(hLng), parseFloat(hLat)] : (charger.location?.coordinates || [])
                  },
                  lat: hLat != null ? parseFloat(hLat) : undefined,
                  lng: hLng != null ? parseFloat(hLng) : undefined,
                  locationLatitude: hLat != null ? parseFloat(hLat) : undefined,
                  locationLongitude: hLng != null ? parseFloat(hLng) : undefined,
                  distanceFromStart: ((i + 1) * distanceKm) / (neededStops + 1),
                  arrivalSOC: Math.max(10, 80 - ((i + 1) * (80 / (neededStops + 2)))),
                  departureSOC: 80,
                  chargingTime: 35,
                  energyAdded: (selectedVehicle.batteryCapacity * 0.5),
                  chargerType: charger.connector_types?.[0] || 'CCS2',
                  power: charger.power || 50,
                  network: charger.network || charger.operator || 'Unknown',
                  heuristic: true
                });
              }
              evRoutePlan.chargingStops = heuristicStops;
            }
            
            // DEBUG: Log actual charging stops to see if they are real or heuristic
          } catch (planError) {
            // Fallback EV calculation with dynamic values
            const totalDistanceKm = routeData.distance;
            const vehicleRangeKm = selectedVehicle.range;
            const batteryCapacityKwh = selectedVehicle.batteryCapacity;
            const consumptionPerKm = adjustedConsumption; // Use calculated adjusted consumption
            
            
            // Calculate energy needed for the trip
            const totalEnergyNeeded = totalDistanceKm * consumptionPerKm;
            const availableEnergy = (batteryPercentage / 100) * batteryCapacityKwh;
            const energyDeficit = Math.max(0, totalEnergyNeeded - availableEnergy);
            
            // Calculate number of charging stops needed
            const stopsNeeded = energyDeficit > 0 ? Math.ceil(totalDistanceKm / vehicleRangeKm) : 0;
            
            
            // Create simple charging stops
            const chargingStops = [];
            if (stopsNeeded > 0) {
              const stopInterval = totalDistanceKm / (stopsNeeded + 1);
              for (let i = 0; i < stopsNeeded; i++) {
                const distanceFromStart = stopInterval * (i + 1);
                chargingStops.push({
                  id: `stop_${i}`,
                  name: `Charging Stop ${i + 1}`,
                  distanceFromStart,
                  arrivalSoC: Math.max(20, batteryPercentage - (distanceFromStart * consumptionPerKm / batteryCapacityKwh * 100)),
                  departureSoC: 80,
                  chargingTime: 30, // Default 30 minutes
                  power: 50, // Default 50kW
                  location: {
                    lat: start.lat + (end.lat - start.lat) * (distanceFromStart / totalDistanceKm),
                    lng: start.lng + (end.lng - start.lng) * (distanceFromStart / totalDistanceKm)
                  },
                  charger: {
                    name: `Charging Stop ${i + 1}`,
                    power: 50,
                    charging_standard: 'CCS'
                  }
                });
              }
            }
            
            evRoutePlan = {
              chargingStops,
              totalChargingTime: chargingStops.length * 30,
              finalSoC: Math.max(20, batteryPercentage - (totalEnergyNeeded / batteryCapacityKwh * 100)),
              energyConsumed: totalEnergyNeeded,
              availableEnergy: availableEnergy,
              requiredEnergy: totalEnergyNeeded,
              energyDeficit: energyDeficit,
              needsCharging: stopsNeeded > 0,
              totalDistance: totalDistanceKm,
              isRoutePossible: true
            };
          }


          // Merge EV route data with basic route data while PRESERVING original distance and duration
          const originalDistance = routeData.distance;
          const originalDuration = routeData.duration;
          
          routeData = {
            ...routeData, // Keep original route, distance, duration
            chargingStops: evRoutePlan.chargingStops || [],
            totalChargingTime: evRoutePlan.totalChargingTime || 0,
            totalTripTime: evRoutePlan.totalTripTime || routeData.duration,
            finalSoC: evRoutePlan.finalSoC || batteryPercentage,
            energyConsumed: evRoutePlan.energyConsumed || 0,
            // Ensure compatibility with RouteDetailsPanel
            selectedVehicle: selectedVehicle,
            vehicleOptions: vehicleOptions,
            batteryPercentage,
            selectedStrategy,
            showAdvancedOptions,
            batteryCapacity: selectedVehicle.batteryCapacity,
            departureSoC: batteryPercentage,
            arrivalSoC,
            stationRadius,
            selectedAmenities,
            mode: 'ev_advanced',
            isEvRoute: true,
            // Force preserve original distance and duration
            distance: originalDistance,
            duration: originalDuration,
            // Add energy plan for RouteDetailsPanel
            energyPlan: {
              totalEnergyNeeded: evRoutePlan.energyConsumed || 0,
              availableEnergy: (batteryPercentage / 100) * selectedVehicle.batteryCapacity,
              energyDeficit: Math.max(0, (evRoutePlan.energyConsumed || 0) - ((batteryPercentage / 100) * selectedVehicle.batteryCapacity)),
              finalSoC: evRoutePlan.finalSoC || batteryPercentage
            }
          };
          // Populate chargingStations ONLY with real stops (filter generic synthetic ones)
          if (Array.isArray(routeData.chargingStops)) {
            const before = routeData.chargingStops.length;
            const realStops = routeData.chargingStops.filter(s => {
              const hasCoords = Number.isFinite(s.lat) && Number.isFinite(s.lng);
              const genericName = typeof s.name === 'string' && /^Charging Stop \d+$/i.test(s.name);
              return hasCoords && !genericName && !s.synthetic && !s.fallback;
            });
            routeData.chargingStations = realStops;
          } else {
            routeData.chargingStations = [];
          }
          

        } catch (chargingError) {
          // Fall back to simple route if charging calculation fails
          routeData.error = 'Smart charging calculation failed, showing simple route';
        }
      }

      if (onCalculateRoute) {
        onCalculateRoute(routeData);
      }
    } catch (error) {
      alert(`Unable to calculate route: ${error.message || error}`);
    } finally {
      if (setIsRoutePlanning) {
        setIsRoutePlanning(false);
      }
    }
  }

  // Dynamic recalculation hook for parameter changes - DISABLED for debugging
  // useEvRouteRecalculation(
  //   {
  //     selectedVehicle,
  //     batteryPercentage,
  //     selectedStrategy,
  //     departureSoC,
  //     arrivalSoC,
  //     stationRadius,
  //     selectedAmenities,
  //     mode,
  //     waypoints: routeWaypoints
  //   },
  //   calculateRoute // recalculation function
  // );

  // Using calculateRoute from context instead of local function



  return (
    <>
      <AnimatePresence>
        {/* Backdrop */}
        <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(4px)',
          zIndex: 1999,
        }}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: -50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: -50 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '5%',
          left: '40%',
          transform: 'translate(-50%, 0)',
          zIndex: 2000,
          width: '90vw',
          maxWidth: 480,
          maxHeight: '95vh',
        }}
      >
                <Paper
                  elevation={12}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: elevatedShadow,
                    backdropFilter: 'blur(10px)',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: panelSurface,
                    border: panelBorder,
                    backgroundImage: isDark
                      ? 'radial-gradient(circle at top, rgba(92,124,255,0.15), transparent 60%)'
                      : 'none',
                  }}
                >
          {/* Header */}
          <Box sx={{ 
            p: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            background: headerGradient,
            borderBottom: isDark
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : '1px solid rgba(0, 0, 0, 0.05)',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: 'visible'
          }}>
            <Box>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                  Plan Your Route
                </Typography>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Find charging stations along your journey
                </Typography>
              </motion.div>
            </Box>
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              style={{
                padding: '8px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: isDark
                  ? alpha(theme.palette.common.white, 0.08)
                  : 'transparent',
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <CloseIcon />
            </motion.button>
          </Box>

                  <Box sx={{ 
                    p: 2.5, 
                    overflow: 'visible',
                    flex: 1,
                    overflowY: 'auto',
                    backgroundColor: isDark ? alpha(theme.palette.background.paper, 0.4) : 'transparent',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: isDark
                        ? alpha(theme.palette.common.white, 0.05)
                        : '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: isDark
                        ? alpha(theme.palette.common.white, 0.2)
                        : '#c1c1c1',
                      borderRadius: '3px',
                      '&:hover': {
                        background: isDark
                          ? alpha(theme.palette.common.white, 0.3)
                          : '#a8a8a8',
                      },
                    },
                  }}>
            {/* Route Type Selection - Animated like webapp */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ marginBottom: 20 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  backgroundColor: toggleBackground,
                  borderRadius: 2,
                  p: 0.5,
                  width: '100%',
                  border: isDark
                    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                    : '1px solid rgba(0,0,0,0.04)'
                }}
              >
                <motion.button
                  type="button"
                  onClick={() => setMode('simple')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '6px',
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: mode === 'simple' ? toggleActiveBg : 'transparent',
                    color: mode === 'simple'
                      ? theme.palette.primary.light
                      : toggleInactiveColor,
                    boxShadow: mode === 'simple'
                      ? (isDark
                          ? `0 6px 18px ${alpha(theme.palette.common.black, 0.7)}`
                          : '0 1px 3px rgba(0,0,0,0.1)')
                      : 'none',
                    transition: 'all 0.2s ease',
                    textTransform: 'none',
                  }}
                >
                  Simple Route
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => setMode('ev_advanced')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '6px',
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: mode === 'ev_advanced' ? toggleActiveBg : 'transparent',
                    color: mode === 'ev_advanced'
                      ? theme.palette.primary.light
                      : toggleInactiveColor,
                    boxShadow: mode === 'ev_advanced'
                      ? (isDark
                          ? `0 6px 18px ${alpha(theme.palette.common.black, 0.7)}`
                          : '0 1px 3px rgba(0,0,0,0.1)')
                      : 'none',
                    transition: 'all 0.2s ease',
                    textTransform: 'none',
                  }}
                >
                  EV Trip Planner
                </motion.button>
              </Box>
            </motion.div>

            {/* Input Fields */}
            <Stack spacing={1.5} sx={{ overflow: 'visible' }}>
              {waypoints.map((waypoint, index) => (
                <motion.div
                  key={waypoint.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                          <Box sx={{ position: 'relative', overflow: 'visible' }}>
                    <TextField
                      fullWidth
                      placeholder={index === 0 ? 'Starting point' : index === waypoints.length - 1 ? 'Destination' : `Stop ${index}`}
                      value={waypoint.location}
                      onChange={(e) => handleLocationInput(waypoint.id, e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: inputBackground,
                          border: `1px solid ${inputBorderColor}`,
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: `2px solid ${theme.palette.primary.main}`,
                          },
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                            '::placeholder': {
                              color: theme.palette.text.secondary,
                              opacity: 0.7,
                            },
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            {index === 0 ? (
                              <GreenDotIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                            ) : (
                              <RedPinIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
                            )}
                          </Box>
                        ),
                        endAdornment: (
                          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                            {index === 0 ? (
                              <IconButton 
                                size="small" 
                                onClick={getCurrentLocation}
                                sx={{ color: theme.palette.success.main }}
                              >
                                <LocationIcon fontSize="small" />
                              </IconButton>
                            ) : index === waypoints.length - 1 ? (
                              <ArrowIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
                            ) : null}
                          </Box>
                        ),
                      }}
                    />
                    
                    {/* Swap Button */}
                    {index === 0 && waypoints.length === 2 && (
                      <Box sx={{ 
                        position: 'absolute', 
                        left: '50%', 
                        top: '100%', 
                        transform: 'translateX(-50%) translateY(-50%)',
                        zIndex: 10
                      }}>
                        <IconButton
                          size="small"
                          onClick={swapWaypoints}
                          sx={{
                            backgroundColor: inputBackground,
                            border: `1px solid ${inputBorderColor}`,
                            boxShadow: isDark
                              ? `0 8px 16px ${alpha(theme.palette.common.black, 0.6)}`
                              : '0 2px 8px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              backgroundColor: isDark
                                ? alpha(theme.palette.common.white, 0.08)
                                : '#f5f5f5',
                            }
                          }}
                        >
                          <SwapIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    )}

                            {/* Predictions Dropdown */}
                            {predictions[waypoint.id]?.length > 0 && (
                              <Paper
                                elevation={8}
                                sx={{
                                  position: 'absolute',
                                  top: 'calc(100% + 8px)',
                                  left: 0,
                                  right: 0,
                                  maxHeight: 200,
                                  overflow: 'auto',
                                  zIndex: 9999,
                                  borderRadius: 2,
                                  border: dropdownBorder,
                                  backgroundColor: dropdownBackground,
                                  boxShadow: isDark
                                    ? `0 18px 40px ${alpha(theme.palette.common.black, 0.75)}`
                                    : '0 8px 32px rgba(0, 0, 0, 0.12)'
                                }}
                              >
                        {predictions[waypoint.id].map(pred => (
                          <Box
                            key={pred.place_id}
                            onClick={() => handleSelectPrediction(waypoint.id, pred)}
                            sx={{
                              p: 2.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                transform: 'translateX(2px)'
                              },
                              borderBottom: isDark
                                ? `1px solid ${alpha(theme.palette.common.white, 0.06)}`
                                : '1px solid #f0f0f0',
                              '&:last-child': {
                                borderBottom: 'none'
                              },
                              '&:first-child': {
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8
                              },
                              // Last child bottom radii merged with previous &:last-child block
                            }}
                          >
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                              {pred.structured_formatting.main_text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {pred.structured_formatting.secondary_text}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                </motion.div>
              ))}
            </Stack>

            {/* EV Advanced Components */}
            {mode === 'ev_advanced' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 16,
                  marginTop: 20
                }}
              >
                {/* Vehicle Selection */}
                <VehicleSelection
                  selectedVehicle={selectedVehicle}
                  batteryPercentage={batteryPercentage}
                  setBatteryPercentage={setBatteryPercentage}
                  onVehicleSelection={handleVehicleSelection}
                />

                {/* Charging Strategy */}
                <ChargingStrategy
                  selectedStrategy={selectedStrategy}
                  onStrategyChange={handleStrategyChange}
                  isCalculating={isRoutePlanning}
                />

                {/* Advanced Options Toggle */}
                <AdvancedOptionsToggle
                  showAdvancedOptions={showAdvancedOptions}
                  setShowAdvancedOptions={setShowAdvancedOptions}
                  showVehicleDetails={showVehicleDetails}
                  setShowVehicleDetails={setShowVehicleDetails}
                />

                {/* Advanced Options */}
                {showAdvancedOptions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  >
                    {/* Vehicle Details Section */}
                    <VehicleDetailsSection
                      selectedVehicle={selectedVehicle}
                      numberOfPassengers={numberOfPassengers}
                      setNumberOfPassengers={setNumberOfPassengers}
                      avgPassengerWeight={avgPassengerWeight}
                      setAvgPassengerWeight={setAvgPassengerWeight}
                      luggageWeight={luggageWeight}
                      setLuggageWeight={setLuggageWeight}
                      vehicleWeight={vehicleWeight}
                      setVehicleWeight={setVehicleWeight}
                      showDetails={showVehicleDetails}
                    />

                    {/* Battery Options */}
                    <BatteryOptionsSection
                      showBatteryOptions={showBatteryOptions}
                      toggleBatteryOptions={() => setShowBatteryOptions(!showBatteryOptions)}
                      batteryCapacity={batteryCapacity}
                      setBatteryCapacity={setBatteryCapacity}
                      departureSoC={departureSoC}
                      setDepartureSoC={setDepartureSoC}
                      arrivalSoC={arrivalSoC}
                      setArrivalSoC={setArrivalSoC}
                      vehicleRange={selectedVehicle?.range || 400}
                    />

                    {/* Station Search Settings */}
                    <StationSearchSettingsSection
                      showStationSettings={showStationSettings}
                      toggleStationSettings={() => setShowStationSettings(!showStationSettings)}
                      stationRadius={stationRadius}
                      setStationRadius={setStationRadius}
                    />

                    {/* Amenities */}
                    <AmenitiesSection
                      showAmenities={showAmenities}
                      toggleAmenities={() => setShowAmenities(!showAmenities)}
                      selectedAmenities={selectedAmenities}
                      setSelectedAmenities={setSelectedAmenities}
                    />
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Plan Route Button - Webapp Style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              style={{ marginTop: mode === 'ev_advanced' ? 20 : 12 }}
            >
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              onClick={() => {
                calculateRoute();
              }}
              disabled={isRoutePlanning || !canPlanRoute}
              whileHover={canPlanRoute ? { scale: 1.02, y: -2 } : {}}
              whileTap={canPlanRoute ? { scale: 0.98 } : {}}
              style={{
                width: '100%',
                padding: '10px 20px',
                marginTop: 12,
                border: 'none',
                borderRadius: 10,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                cursor: canPlanRoute ? 'pointer' : 'not-allowed',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.3s ease',
                background: canPlanRoute ? brandGradient : disabledButtonBg,
                color: canPlanRoute ? theme.palette.common.white : disabledButtonColor,
                boxShadow: canPlanRoute 
                  ? brandShadow
                  : '0 1px 4px rgba(15, 23, 42, 0.12)',
                opacity: (isRoutePlanning || !canPlanRoute) ? 0.6 : 1,
                pointerEvents: (isRoutePlanning || !canPlanRoute) ? 'none' : 'auto',
              }}
              onMouseEnter={(e) => {
                if (canPlanRoute) {
                  e.target.style.background = brandHoverGradient;
                  e.target.style.boxShadow = '0 18px 35px rgba(37, 99, 235, 0.35)';
                }
              }}
              onMouseLeave={(e) => {
                if (canPlanRoute) {
                  e.target.style.background = brandGradient;
                  e.target.style.boxShadow = brandShadow;
                }
              }}
            >
              {/* Shimmer Effect */}
              {canPlanRoute && (
                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)',
                  }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
              
              {/* Button Content */}
              <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {isRoutePlanning ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{
                      width: 20,
                      height: 20,
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  <>
                  <LightningIcon style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>
                      {mode === 'simple' ? 'Plan Route' : 'Plan EV Advanced Trip'}
                    </span>
                  </>
                )}
              </div>
            </motion.button>
            </motion.div>
          </Box>
        </Paper>
      </motion.div>

      {/* Vehicle Selection Modal - Replaced with inline selection */}
      {/* <EnhancedVehicleSelectionModal
        open={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        onVehicleSelect={handleVehicleSelect}
        selectedVehicle={selectedVehicle}
      /> */}
      </AnimatePresence>

      {/* Vehicle Selection Modal - Rendered via Portal */}
      {showVehicleSelectionModal && ReactDOM.createPortal(
        <Dialog
          open={showVehicleSelectionModal}
          onClose={() => setShowVehicleSelectionModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '80vh',
              minHeight: '60vh',
              zIndex: '99999 !important',
              position: 'fixed',
              margin: 2
            }
          }}
          BackdropProps={{
            sx: { 
              zIndex: '99998 !important',
              position: 'fixed'
            }
          }}
          disablePortal={false}
          style={{
            zIndex: 99999
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <InlineVehicleSelection
              selectedVehicle={selectedVehicle}
              onVehicleSelect={(vehicle) => {
                handleVehicleSelect(vehicle);
                setShowVehicleSelectionModal(false);
              }}
              onClose={() => setShowVehicleSelectionModal(false)}
            />
          </DialogContent>
        </Dialog>,
        document.body
      )}
    </>
  );
};

export default RoutePlannerPanel;
